// A project URL's ref is a branch OR a commit (`/p/:slug/b/:ref/...`; the api resolves it: a branch of
// that name wins, else 7–40 hex naming a commit; the head sha of a branch is that branch). At a commit the whole project view is that commit,
// read-only: the api answers every page's design reads from the commit and refuses writes (409
// read_only_commit); runtime state (runs, instances, versions) is its HOME branch's.
//
// This module: resolves the URL ref (cached), remembers the last branch the person was on per project
// (the home-branch hint, a per-viewer convenience in sessionStorage), sends that hint on every api call
// (`x-cortex-branch`), toasts a refused write, and marks write buttons disabled while at a commit.
import { toast } from '@cortex/ui';
import * as React from 'react';
import { api } from '../api.ts';

export interface RefInfo {
  ref: string;
  kind: 'branch' | 'commit';
  sha: string;
  short: string;
  /** The ref itself for a branch; the home branch for a commit. */
  branch: string;
  defaultBranch: string;
  commit: { sha: string; message: string; date: string; author: { name: string; email: string } } | null;
  isHead: boolean;
}

export const COMMIT_REF = /^[0-9a-f]{7,40}$/;
export const READ_ONLY_TITLE = 'Viewing a commit: switch to a branch to change this';

const KEY = 'cortex.lastBranch.';
function lastBranch(slug: string): string | null {
  try {
    return sessionStorage.getItem(KEY + slug);
  } catch {
    return null;
  }
}
function rememberBranch(slug: string, branch: string) {
  try {
    sessionStorage.setItem(KEY + slug, branch);
  } catch {
    /* storage unavailable: the api falls back to the default branch */
  }
}

// The branch hint for the api: set by the provider from the URL.
let hint: string | null = null;
let atCommit = false;
let installed = false;
function installMiddleware() {
  if (installed) return;
  installed = true;
  api.use({
    onRequest({ request }) {
      if (hint) request.headers.set('x-cortex-branch', hint);
      return request;
    },
    async onResponse({ response }) {
      if (response.status === 409 && atCommit) {
        const body = (await response
          .clone()
          .json()
          .catch(() => null)) as { error?: string; message?: string } | null;
        if (body?.error === 'read_only_commit')
          toast.error(body.message ?? 'Switch to a branch to change this');
      }
      return response;
    },
  });
}

const cache = new Map<string, Promise<RefInfo | null>>();
export function resolveRef(slug: string, ref: string, prefer: string | null): Promise<RefInfo | null> {
  const k = `${slug}\0${ref}\0${prefer ?? ''}`;
  let p = cache.get(k);
  if (!p) {
    p = api
      .GET('/api/git/projects/{slug}/code/resolve', {
        params: { path: { slug }, query: { ref, ...(prefer ? { prefer } : {}) } },
      })
      .then(({ data }) => (data as RefInfo | undefined) ?? null)
      .catch(() => null);
    cache.set(k, p);
    // A branch's answer moves with the branch: keep it briefly. So does a sha that is its branch's head (the api
    // answers it as the branch, writable, until the branch moves on); an older commit is immutable.
    if (!COMMIT_REF.test(ref)) setTimeout(() => cache.delete(k), 10_000);
    void p.then((v) => {
      if (v === null) cache.delete(k);
      else if (v.kind === 'branch' && COMMIT_REF.test(ref)) setTimeout(() => cache.delete(k), 10_000);
    });
  }
  return p;
}

const Ctx = React.createContext<{ info: RefInfo | null; atCommit: boolean }>({ info: null, atCommit: false });
export const useCommitView = () => React.useContext(Ctx);

/** Wraps the project shell: knows whether the URL ref is a commit. */
export function CommitViewProvider({
  slug,
  refName,
  children,
}: {
  slug: string | null;
  refName: string | null;
  children: React.ReactNode;
}) {
  installMiddleware();
  const maybeCommit = Boolean(refName && COMMIT_REF.test(refName));
  const [info, setInfo] = React.useState<RefInfo | null>(null);

  React.useEffect(() => {
    if (!slug || !refName) {
      setInfo(null);
      hint = null;
      atCommit = false;
      return;
    }
    if (!maybeCommit) {
      rememberBranch(slug, refName);
      hint = refName;
      atCommit = false;
      setInfo(null);
      return;
    }
    let alive = true;
    const prefer = lastBranch(slug);
    hint = prefer;
    void resolveRef(slug, refName, prefer).then((r) => {
      if (!alive) return;
      setInfo(r);
      atCommit = r?.kind === 'commit';
      // The head of a branch is that branch (writable): remember the branch, never the sha.
      if (r?.kind === 'branch') {
        rememberBranch(slug, r.branch);
        hint = r.branch;
      }
    });
    return () => {
      alive = false;
    };
  }, [slug, refName, maybeCommit]);

  const value = React.useMemo(
    () => ({ info: maybeCommit ? info : null, atCommit: maybeCommit && info?.kind === 'commit' }),
    [info, maybeCommit],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Words that name a change: a button starting with one of them is a write. */
const WRITE =
  /^(?:(?:new|create|add|save|delete|remove|edit|rename|run|import|upload|publish|merge|promote|train|start|stop|retry|approve|reject|complete|submit|apply|install|connect|disconnect|update|change|move|duplicate|archive|restore|reset|generate|draft|describe|suggest|sync|commit|revert|share|invite|set|activate|deploy|score|build)\b|\+)/i;

/**
 * While at a commit, disables every button in `root` whose label names a change, with the read-only
 * tooltip (the api refuses the write anyway; this keeps people from trying). Navigation, filters and
 * tabs keep working. Restores the buttons when the view is back on a branch.
 */
export function useReadOnlyButtons(root: React.RefObject<HTMLElement | null>, on: boolean) {
  React.useEffect(() => {
    const el = root.current;
    if (!el || !on) return;
    const mark = () => {
      for (const b of el.querySelectorAll<HTMLButtonElement>('button:not([data-commit-safe])')) {
        if (b.dataset.commitReadonly) continue;
        const label = (b.getAttribute('aria-label') || b.textContent || '').trim();
        if (!WRITE.test(label)) continue;
        b.dataset.commitReadonly = b.disabled ? 'was-disabled' : '1';
        b.dataset.commitTitle = b.title;
        b.disabled = true;
        b.title = READ_ONLY_TITLE;
      }
    };
    mark();
    const mo = new MutationObserver(mark);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      for (const b of el.querySelectorAll<HTMLButtonElement>('button[data-commit-readonly]')) {
        if (b.dataset.commitReadonly !== 'was-disabled') b.disabled = false;
        b.title = b.dataset.commitTitle ?? '';
        delete b.dataset.commitReadonly;
        delete b.dataset.commitTitle;
      }
    };
  }, [root, on]);
}
